import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, ExternalLink, Users, UserPlus, BedDouble } from 'lucide-react';
import { api, type Agenda as AgendaDatos, type Contacto } from '@/lib/api';
import BotonLlamar from './BotonLlamar';

/**
 * A quién se puede llamar desde el CRM.
 *
 * La agenda entera se pide una vez y se filtra en el navegador. Son unos
 * cientos de fichas, y lo que se hace con una agenda es teclear tres letras y
 * encontrar a alguien: ir al servidor en cada tecla añadiría espera sin ganar
 * nada. Se busca a la vez por nombre y por teléfono, porque a veces lo que se
 * tiene es un número en una nota y lo que se quiere saber es de quién es.
 */

const CATEGORIAS = [
  { clave: 'propietario', etiqueta: 'Propietarios', icono: Users },
  { clave: 'lead_propietario', etiqueta: 'Leads', icono: UserPlus },
  { clave: 'huesped', etiqueta: 'Huéspedes', icono: BedDouble },
] as const;

/** Quita acentos y mayúsculas: buscar "Nunez" tiene que encontrar a "Núñez". */
function plano(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

export default function Agenda() {
  const [datos, setDatos] = useState<AgendaDatos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<Contacto['categoria']>('propietario');
  const [busqueda, setBusqueda] = useState('');
  /**
   * Los huéspedes se piden la primera vez que se abre su pestaña, no al cargar
   * la página: son 4.441 con teléfono y unos 740 KB, y traerlos siempre haría
   * más lento abrir la centralita para llamar a un propietario.
   */
  const [huespedes, setHuespedes] = useState<Contacto[] | null>(null);
  const [cargandoHuespedes, setCargandoHuespedes] = useState(false);

  useEffect(() => {
    if (categoria !== 'huesped' || huespedes || cargandoHuespedes) return;
    setCargandoHuespedes(true);
    api
      .getContactosHuespedes()
      .then((d) => setHuespedes(d.items))
      .catch((e) => setError(e instanceof Error ? e.message : 'Error cargando los huéspedes'))
      .finally(() => setCargandoHuespedes(false));
  }, [categoria, huespedes, cargandoHuespedes]);

  useEffect(() => {
    let cancelado = false;
    api
      .getContactos()
      .then((d) => { if (!cancelado) setDatos(d); })
      .catch((e) => { if (!cancelado) setError(e instanceof Error ? e.message : 'Error cargando la agenda'); })
      .finally(() => { if (!cancelado) setCargando(false); });
    return () => { cancelado = true; };
  }, []);

  const visibles = useMemo(() => {
    const todos = categoria === 'huesped' ? (huespedes ?? []) : (datos?.items ?? []);
    const q = plano(busqueda.trim());
    const digitos = busqueda.replace(/\D/g, '');
    return todos.filter((c) => {
      if (c.categoria !== categoria) return false;
      if (!q) return true;
      if (plano(c.nombre).includes(q)) return true;
      if (c.detalle && plano(c.detalle).includes(q)) return true;
      // Con tres dígitos ya se puede buscar por teléfono sin devolver medio listín.
      if (digitos.length >= 3 && [c.telefono, c.telefonoAlt].some((t) => t?.replace(/\D/g, '').includes(digitos))) {
        return true;
      }
      return false;
    });
  }, [datos, huespedes, categoria, busqueda]);

  return (
    <div className="card p-0 overflow-hidden flex flex-col">
      <div className="p-3 border-b border-slate-100 dark:border-white/5 space-y-2.5">
        <div className="flex gap-1">
          {CATEGORIAS.map(({ clave, etiqueta, icono: Icono }) => (
            <button
              key={clave}
              onClick={() => setCategoria(clave)}
              className={`flex items-center gap-1.5 min-h-9 px-3 text-sm rounded-md transition-colors ${
                categoria === clave
                  ? 'bg-sereno-dark text-white'
                  : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5'
              }`}
            >
              <Icono size={14} />
              {etiqueta}
              {/* El de huéspedes solo se sabe una vez cargados, así que hasta
                  entonces no se pinta número en vez de pintar un cero falso. */}
              {(clave === 'huesped' ? huespedes !== null : Boolean(datos)) && (
                <span className={categoria === clave ? 'text-white/70' : 'text-slate-400'}>
                  {clave === 'huesped' ? huespedes?.length : datos?.porCategoria[clave]}
                </span>
              )}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
          <input
            className="input pl-9"
            placeholder="Nombre o teléfono…"
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
          />
        </div>
      </div>

      <div className="max-h-[420px] overflow-y-auto">
        {cargando || cargandoHuespedes ? (
          <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" size={22} /></div>
        ) : error ? (
          <p className="p-4 text-sm text-amber-700 dark:text-amber-300">{error}</p>
        ) : !visibles.length ? (
          <p className="p-6 text-sm text-slate-500 text-center">
            {busqueda ? 'Nadie coincide con la búsqueda.' : 'No hay contactos con teléfono en esta categoría.'}
          </p>
        ) : (
          <ul className="divide-y divide-slate-100 dark:divide-white/5">
            {visibles.map((c) => (
              <li key={`${c.categoria}-${c.id}`} className="flex items-center gap-2 px-3 py-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-slate-800 dark:text-slate-100 truncate">{c.nombre}</p>
                  <p className="text-xs text-slate-400 truncate">
                    {c.telefono}
                    {c.detalle && ` · ${c.detalle}`}
                  </p>
                </div>
                <Link
                  to={c.ficha}
                  className="btn-ghost px-2 shrink-0"
                  title="Abrir su ficha"
                  aria-label={`Abrir la ficha de ${c.nombre}`}
                >
                  <ExternalLink size={14} />
                </Link>
                <BotonLlamar numero={c.telefono} nombre={c.nombre} alojamiento={c.detalle} compacto />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
